import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import LoadingTimeout from '../components/LoadingTimeout';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { useRegisterRefresh } from '../context/RefreshContext';
import { supabase } from '../lib/supabaseClient';
import { meses, relatorios, withAuthRetry } from '../lib/api';
import { useMonth } from '../context/MonthContext';
import { formatCurrency, CHART_COLORS } from '../utils/format';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Receipt,
  PiggyBank,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

function DashboardPage() {
  const navigate = useNavigate();
  const { mesData, resumo, loading, currentMonth, refreshData } = useMonth();
  const [previousResumo, setPreviousResumo] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { timedOut, resetTimeout } = useLoadingTimeout(loading, refreshData);
  useRegisterRefresh(refreshData);

  // Show success toast after returning from Stripe Checkout
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      toast.success('Pagamento confirmado! Sua assinatura está ativa.');
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // If user signed in after clicking an upgrade CTA, resume that checkout
  useEffect(() => {
    const raw = sessionStorage.getItem('pendingCheckout');
    if (!raw) return;
    let pending;
    try {
      pending = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem('pendingCheckout');
      return;
    }
    sessionStorage.removeItem('pendingCheckout');
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          'create-checkout-session',
          { body: pending },
        );
        if (error) throw error;
        if (data?.url) window.location.href = data.url;
      } catch (err) {
        console.error('Resume checkout failed:', err);
      }
    })();
  }, []);

  // Load previous month for comparison — only after current month is loaded
  // to avoid racing with MonthContext on Supabase's auth lock.
  useEffect(() => {
    if (!mesData?.id) return;
    let cancelled = false;

    (async () => {
      try {
        const prevMes = currentMonth.mes === 1 ? 12 : currentMonth.mes - 1;
        const prevAno = currentMonth.mes === 1 ? currentMonth.ano - 1 : currentMonth.ano;

        const mesResponse = await withAuthRetry(() => meses.getOrCreate(prevMes, prevAno));
        if (cancelled) return;
        if (mesResponse?.id) {
          const resumoResponse = await withAuthRetry(() =>
            relatorios.getResumoMensal(mesResponse.id)
          );
          if (!cancelled) setPreviousResumo(resumoResponse);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load previous month:', error);
          setPreviousResumo(null);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [mesData?.id, currentMonth.mes, currentMonth.ano]);

  // Prepare chart data
  const chartData = resumo?.despesas_por_categoria 
    ? Object.entries(resumo.despesas_por_categoria).map(([name, value], index) => ({
        name,
        value,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }))
    : [];

  // Calculate comparison with previous month
  const getDifference = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const despesasDiff = previousResumo 
    ? getDifference(resumo?.total_despesas || 0, previousResumo.total_despesas)
    : null;

  // Determine saldo status
  const getSaldoStatus = () => {
    if (!resumo) return 'neutral';
    if (!resumo.total_bruto || resumo.total_bruto === 0) return 'no-receitas';
    const ratio = resumo.total_liquido > 0
      ? resumo.saldo_mensal / resumo.total_liquido 
      : 0;
    if (resumo.saldo_mensal < 0) return 'negative';
    if (ratio < 0.05) return 'warning';
    return 'positive';
  };

  const saldoStatus = getSaldoStatus();

  if (loading) {
    return (
      <Layout>
        {timedOut ? (
          <LoadingTimeout
            timedOut={timedOut}
            onRetry={() => {
              resetTimeout();
              refreshData();
            }}
          />
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1B2A4A] border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-slate-600">Carregando dados...</p>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="dashboard">
        {/* Health Indicator Banner */}
        {resumo && (
          <div 
            className={`p-5 rounded-2xl border-2 flex items-center gap-4 ${
              saldoStatus === 'positive'
                ? 'bg-green-50 border-green-300 text-green-800'
                : saldoStatus === 'warning'
                ? 'bg-amber-50 border-amber-300 text-amber-800'
                : saldoStatus === 'no-receitas'
                ? 'bg-blue-50 border-blue-300 text-blue-800'
                : 'bg-red-50 border-red-300 text-red-800'
            }`}
            data-testid="health-indicator"
          >
            {saldoStatus === 'no-receitas' ? (
              <>
                <AlertTriangle size={32} />
                <div>
                  <p className="text-xl font-bold">Comece cadastrando suas receitas</p>
                  <p className="text-lg">Adicione suas receitas para visualizar seu resumo financeiro.</p>
                </div>
              </>
            ) : saldoStatus === 'positive' ? (
              <>
                <CheckCircle size={32} />
                <div>
                  <p className="text-xl font-bold">Seu bolso está no controle!</p>
                  <p className="text-lg">Sobraram {formatCurrency(resumo.saldo_mensal)} este mês</p>
                </div>
              </>
            ) : saldoStatus === 'warning' ? (
              <>
                <AlertTriangle size={32} />
                <div>
                  <p className="text-xl font-bold">Atenção!</p>
                  <p className="text-lg">Sua margem está apertada: {formatCurrency(resumo.saldo_mensal)}</p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle size={32} />
                <div>
                  <p className="text-xl font-bold">Cuidado!</p>
                  <p className="text-lg">Suas despesas superam sua receita em {formatCurrency(Math.abs(resumo.saldo_mensal))}</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Main Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Receita Bruta */}
          <Card
            onClick={() => navigate('/receitas')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/receitas')}
            className="border-2 border-slate-200 shadow-lg hover:border-[#1B2A4A] hover:shadow-xl cursor-pointer active:scale-[0.99] transition-all"
            data-testid="card-receita-bruta"
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-lg font-semibold text-slate-600">
                <div className="w-10 h-10 bg-[#1B2A4A] rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                Receita Bruta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-extrabold text-[#1B2A4A] tabular-nums">
                {formatCurrency(resumo?.total_bruto || 0)}
              </p>
            </CardContent>
          </Card>

          {/* Total Descontos */}
          <Card
            onClick={() => navigate('/receitas')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/receitas')}
            className="border-2 border-slate-200 shadow-lg hover:border-[#1B2A4A] hover:shadow-xl cursor-pointer active:scale-[0.99] transition-all"
            data-testid="card-descontos"
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-lg font-semibold text-slate-600">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-white" />
                </div>
                Total Descontos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-extrabold text-red-600 tabular-nums">
                - {formatCurrency(resumo?.total_descontos || 0)}
              </p>
            </CardContent>
          </Card>

          {/* Receita Líquida */}
          <Card
            onClick={() => navigate('/receitas')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/receitas')}
            className="border-2 border-green-300 bg-green-50 shadow-lg hover:border-green-600 hover:shadow-xl cursor-pointer active:scale-[0.99] transition-all"
            data-testid="card-receita-liquida"
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-lg font-semibold text-green-700">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                Receita Líquida
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-extrabold text-green-700 tabular-nums">
                {formatCurrency(resumo?.total_liquido || 0)}
              </p>
            </CardContent>
          </Card>

          {/* Total Despesas */}
          <Card
            onClick={() => navigate('/despesas')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/despesas')}
            className="border-2 border-slate-200 shadow-lg hover:border-[#1B2A4A] hover:shadow-xl cursor-pointer active:scale-[0.99] transition-all"
            data-testid="card-despesas"
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-lg font-semibold text-slate-600">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                Total Despesas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-extrabold text-[#1B2A4A] tabular-nums">
                {formatCurrency(resumo?.total_despesas || 0)}
              </p>
              {despesasDiff !== null && (
                <p className={`text-base font-semibold flex items-center gap-1 mt-2 ${despesasDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {despesasDiff > 0 ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                  {Math.abs(despesasDiff).toFixed(1)}% vs mês anterior
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Saldo Mensal - Highlighted */}
        <Card
          onClick={() => navigate('/relatorios')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/relatorios')}
          className={`border-2 shadow-xl cursor-pointer active:scale-[0.99] transition-all hover:shadow-2xl ${
            saldoStatus === 'positive'
              ? 'border-green-400 bg-gradient-to-br from-green-50 to-green-100 hover:border-green-500'
              : saldoStatus === 'warning'
              ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100 hover:border-amber-500'
              : 'border-red-400 bg-gradient-to-br from-red-50 to-red-100 hover:border-red-500'
          }`}
          data-testid="card-saldo"
        >
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-3 text-xl font-bold ${
              saldoStatus === 'positive' ? 'text-green-700' : saldoStatus === 'warning' ? 'text-amber-700' : 'text-red-700'
            }`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                saldoStatus === 'positive' ? 'bg-green-600' : saldoStatus === 'warning' ? 'bg-amber-500' : 'bg-red-600'
              }`}>
                <PiggyBank className="w-6 h-6 text-white" />
              </div>
              Saldo Mensal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-4xl md:text-5xl font-extrabold tabular-nums ${
              saldoStatus === 'positive' ? 'text-green-700' : saldoStatus === 'warning' ? 'text-amber-700' : 'text-red-700'
            }`}>
              {formatCurrency(resumo?.saldo_mensal || 0)}
            </p>
            <p className="text-lg text-slate-600 mt-2">
              = Receita Líquida - Total Despesas
            </p>
          </CardContent>
        </Card>

        {/* Expenses Progress */}
        {resumo && resumo.despesas_total > 0 && (
          <Card className="border-2 border-slate-200 shadow-lg" data-testid="card-progress">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-[#1B2A4A]">
                Despesas Pagas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <span className="text-2xl font-bold text-[#1B2A4A]">
                  {resumo.despesas_pagas} de {resumo.despesas_total}
                </span>
                <span className="text-lg font-semibold text-slate-600">
                  {Math.round((resumo.despesas_pagas / resumo.despesas_total) * 100)}%
                </span>
              </div>
              <Progress 
                value={(resumo.despesas_pagas / resumo.despesas_total) * 100} 
                className="h-6 bg-slate-200"
              />
            </CardContent>
          </Card>
        )}

        {/* Expense Breakdown Chart */}
        {chartData.length > 0 && (
          <Card className="border-2 border-slate-200 shadow-lg" data-testid="card-chart">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-[#1B2A4A]">
                Despesas por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        fontSize: '16px',
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Custom legend — one row per category, easy to read */}
              <ul className="mt-4 space-y-2">
                {(() => {
                  const total = chartData.reduce((s, c) => s + c.value, 0) || 1;
                  return chartData
                    .slice()
                    .sort((a, b) => b.value - a.value)
                    .map((entry) => {
                      const pct = Math.round((entry.value / total) * 100);
                      return (
                        <li
                          key={entry.name}
                          className="flex items-center justify-between gap-3 p-2 rounded-lg bg-slate-50"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className="w-4 h-4 rounded-sm shrink-0"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-base font-medium text-[#1B2A4A] truncate">
                              {entry.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-base font-semibold tabular-nums text-[#1B2A4A]">
                              {formatCurrency(entry.value)}
                            </span>
                            <span className="text-sm font-medium text-slate-500 tabular-nums w-10 text-right">
                              {pct}%
                            </span>
                          </div>
                        </li>
                      );
                    });
                })()}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {resumo && resumo.total_bruto === 0 && resumo.total_despesas === 0 && (
          <Card className="border-2 border-dashed border-slate-300 bg-slate-50">
            <CardContent className="py-12 text-center">
              <Wallet size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-xl font-bold text-slate-600 mb-2">
                Nenhum dado cadastrado
              </h3>
              <p className="text-lg text-slate-500">
                Comece adicionando suas receitas e despesas deste mês
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default DashboardPage;
